import { extendTheme, CssVarsThemeOptions, ThemeCssVarOverrides } from '@mui/material/styles';
import { typography } from './typographyTheme';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import MallColor from '@/helpers/MallColor';

export const options: ThemeCssVarOverrides = {
    cssVarPrefix: 'v',
    typography: {
        fontFamily: "'Noto Sans KR', sans-serif",
        ...typography,
        // 재플린 기준 36px 폰트 사이즈를 16px(1rem)으로 계산 후 적용함
        h1: {
            fontSize: 'var(--v-theme-typography-h1-size, 1.188rem)',
            lineHeight: 'var(--v-theme-typography-h1-line-height, 1.167)',
            letterSpacing: 'var(--v-theme-typography-h1-letter-spacing, -0.01562em)',
        },
        h2: {
            fontSize: 'var(--v-theme-typography-h2-size, 1.125rem)',
            lineHeight: 'var(--v-theme-typography-h2-line-height, 1.2)',
            letterSpacing: 'var(--v-theme-typography-h2-letter-spacing, -0.00833em)',
        },
        h3: {
            fontSize: 'var(--v-theme-typography-h3-size, 1rem)',
            lineHeight: 'var(--v-theme-typography-h3-line-height, 1.167)',
            letterSpacing: 'var(--v-theme-typography-h3-letter-spacing, 0em)',
        },
        h4: {
            fontSize: 'var(--v-theme-typography-h4-size, .875rem)',
            lineHeight: 'var(--v-theme-typography-h3-line-height, 1.235)',
            letterSpacing: 'var(--v-theme-typography-h4-letter-spacing, 0.00735em)',
        },
        h5: {
            fontSize: 'var(--v-theme-typography-h5-size, 0.75rem)',
            lineHeight: 'var(--v-theme-typography-h5-line-height, 1.334)',
            letterSpacing: 'var(--v-theme-typography-h5-letter-spacing, 0em)',
        },
        h6: {
            fontSize: 'var(--v-theme-typography-h6-size, 0.625rem)',
            lineHeight: 'var(--v-theme-typography-h6-line-height, 1.6)',
            letterSpacing: 'var(--v-theme-typography-h6-letter-spacing, 0.00938em)',
        },
        body1: {
            fontSize: 'var(--v-theme-typography-body1-size, 1rem)',
            lineHeight: 'var(--v-theme-typography-body1-line-height, 1.5)',
            letterSpacing: 'var(--v-theme-typography-body1-letter-spacing, 0.00938em)',
        },
        body2: {
            fontSize: 'var(--v-theme-typography-body2-size, 0.875rem)',
            lineHeight: 'var(--v-theme-typography-body2-line-height, 1.43)',
            letterSpacing: 'var(--v-theme-typography-body2-letter-spacing, 0.01071em)',
        },
        subtitle1: {
            fontSize: 'var(--v-theme-typography-subtitle1-size, .875rem)',
            lineHeight: 'var(--v-theme-typography-subtitle1-line-height, 1.75)',
            letterSpacing: 'var(--v-theme-typography-subtitle1-letter-spacing, 0.00938em)',
        },
        subtitle2: {
            fontSize: 'var(--v-theme-typography-subtitle2-size, 0.75rem)',
            lineHeight: 'var(--v-theme-typography-subtitle2-line-height, 1.57)',
            letterSpacing: 'var(--v-theme-typography-subtitle2-letter-spacing, 0.00714em)',
        },
        caption: {
            fontSize: 'var(--v-theme-typography-caption-size, 0.6rem)',
            lineHeight: 'var(--v-theme-typography-caption-line-height, 1.66)',
            letterSpacing: 'var(--v-theme-typography-caption-letter-spacing, 0.03333em)',
        },
        overline: {
            fontSize: 'var(--v-theme-typography-overline-size, 0.75rem)',
            lineHeight: 'var(--v-theme-typography-overline-line-height, 2.66)',
            letterSpacing: 'var(--v-theme-typography-overline-letter-spacing, 0.08333em)',
        },
    },
    shape: {
        borderRadius: 3,
    },
    colorSchemes: {
        light: {
            palette: {
                primary: {
                    main: '#ff6aac',
                    contrastText: '#fff',
                },
                custom: {
                    light: '#757ce8',
                    main: '#000000',
                    dark: '#002884',
                    contrastText: '#fff',
                },
                success: {
                    main: '#2e7d32',
                    contrastText: '#fff',
                },
                error: {
                    main: '#d32f2f',
                    contrastText: '#fff',
                },
                warning: {
                    main: '#ed6c02',
                    contrastText: '#fff',
                },
                info: {
                    main: '#0288d1',
                    contrastText: '#fff',
                },
                orange: {
                    main: '#ff9800',
                    light: '#ffb74d',
                    dark: '#f57c00',
                    contrastText: '#fff',
                },
                'deeper-orange': {
                    main: '#FB6D48',
                    light: '#FF9A7A',
                    dark: '#E55A3B',
                    contrastText: '#fff',
                },
                'not-active': {
                    main: '#888888',
                    light: '#bdbdbd',
                    dark: '#555555',
                    contrastText: '#fff',
                },
            },
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {},
        },
        MuiButton: {
            defaultProps: {
                color: 'primary',
            },
            styleOverrides: {
                root: {
                    borderRadius: 0,
                    minHeight: 0,
                },
            },
            variants: [
                {
                    props: { color: 'custom' },
                    style: {
                        wordBreak: 'keep-all',
                        borderColor: `${MallColor['button-border-gray']}`,
                        fontSize: 'var(--v-theme-typography-h5-size, 0.75rem)',
                    },
                },
            ],
        },
        MuiCheckbox: {
            // 재플린 기준으로는 체크마크랑, border color가 다름. 확인 필요
            defaultProps: {
                size: 'small',
                icon: <CheckBoxOutlinedIcon />,
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    color: `${MallColor['input-checkbox-border-gray']}`,
                    '&.Mui-checked': {
                        color: MallColor['input-checkbox-bg-blue'],
                    },
                },
            },
        },
        MuiDialog: {
            defaultProps: {
                PaperProps: {
                    style: {
                        borderRadius: 0,
                        boxShadow: 'none',
                        minWidth: '300px',
                    },
                },
                slotProps: {
                    backdrop: {
                        style: {
                            opacity: 0.5,
                        },
                    },
                },
            },
        },
    },
};

const theme = extendTheme(options);

export default theme;
